import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BucketConfigModal = ({ isOpen, onClose, onSave, editBucket }) => {
  const [formData, setFormData] = useState({
    name: '',
    bucketName: '',
    region: 'us-east-1',
    accessKey: '',
    secretKey: '',
    publicUrl: ''
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (editBucket) {
      setFormData({
        name: editBucket?.name || '',
        bucketName: editBucket?.bucketName || '',
        region: editBucket?.region || 'us-east-1',
        accessKey: editBucket?.accessKey || '',
        secretKey: '', // Always clear secret key for security
        publicUrl: editBucket?.publicUrl || ''
      });
    } else {
      setFormData({
        name: '',
        bucketName: '',
        region: 'us-east-1',
        accessKey: '',
        secretKey: '',
        publicUrl: ''
      });
    }
    setErrors({});
    setSaveError('');
    setTestResult(null);
  }, [editBucket, isOpen]);

  // Complete list of all AWS regions
  const regionOptions = [
    // US Regions
    { value: 'us-east-1', label: 'US East (N. Virginia)', description: 'us-east-1' },
    { value: 'us-east-2', label: 'US East (Ohio)', description: 'us-east-2' },
    { value: 'us-west-1', label: 'US West (N. California)', description: 'us-west-1' },
    { value: 'us-west-2', label: 'US West (Oregon)', description: 'us-west-2' },
    
    // Africa
    { value: 'af-south-1', label: 'Africa (Cape Town)', description: 'af-south-1' },
    
    // Asia Pacific
    { value: 'ap-east-1', label: 'Asia Pacific (Hong Kong)', description: 'ap-east-1' },
    { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)', description: 'ap-south-1' },
    { value: 'ap-south-2', label: 'Asia Pacific (Hyderabad)', description: 'ap-south-2' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)', description: 'ap-southeast-1' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)', description: 'ap-southeast-2' },
    { value: 'ap-southeast-3', label: 'Asia Pacific (Jakarta)', description: 'ap-southeast-3' },
    { value: 'ap-southeast-4', label: 'Asia Pacific (Melbourne)', description: 'ap-southeast-4' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)', description: 'ap-northeast-1' },
    { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)', description: 'ap-northeast-2' },
    { value: 'ap-northeast-3', label: 'Asia Pacific (Osaka)', description: 'ap-northeast-3' },
    
    // Canada
    { value: 'ca-central-1', label: 'Canada (Central)', description: 'ca-central-1' },
    { value: 'ca-west-1', label: 'Canada (Calgary)', description: 'ca-west-1' },
    
    // Europe
    { value: 'eu-central-1', label: 'Europe (Frankfurt)', description: 'eu-central-1' },
    { value: 'eu-central-2', label: 'Europe (Zurich)', description: 'eu-central-2' },
    { value: 'eu-west-1', label: 'Europe (Ireland)', description: 'eu-west-1' },
    { value: 'eu-west-2', label: 'Europe (London)', description: 'eu-west-2' },
    { value: 'eu-west-3', label: 'Europe (Paris)', description: 'eu-west-3' },
    { value: 'eu-north-1', label: 'Europe (Stockholm)', description: 'eu-north-1' },
    { value: 'eu-south-1', label: 'Europe (Milan)', description: 'eu-south-1' },
    { value: 'eu-south-2', label: 'Europe (Spain)', description: 'eu-south-2' },
    
    // Israel
    { value: 'il-central-1', label: 'Israel (Tel Aviv)', description: 'il-central-1' },
    
    // Middle East
    { value: 'me-south-1', label: 'Middle East (Bahrain)', description: 'me-south-1' },
    { value: 'me-central-1', label: 'Middle East (UAE)', description: 'me-central-1' },
    
    // South America
    { value: 'sa-east-1', label: 'South America (São Paulo)', description: 'sa-east-1' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (saveError) {
      setSaveError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Configuration name is required';
    if (!formData?.bucketName?.trim()) newErrors.bucketName = 'Bucket name is required';
    if (!formData?.accessKey?.trim()) newErrors.accessKey = 'Access key is required';
    if (!editBucket && !formData?.secretKey?.trim()) newErrors.secretKey = 'Secret key is required';
    if (!formData?.publicUrl?.trim()) newErrors.publicUrl = 'Public URL is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;

    setIsTesting(true);
    setTestResult(null);
    setSaveError('');

    try {
      // Simulate S3 connection test
      // In a real implementation, this would make an actual request to test the connection
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Validate bucket configuration format
      const isValidConfig = 
        formData?.bucketName?.trim()?.length > 0 &&
        formData?.accessKey?.trim()?.length > 0 &&
        formData?.publicUrl?.trim()?.match(/^https?:\/\/.+/i);

      if (isValidConfig) {
        setTestResult({
          success: true,
          message: 'Connection test successful! Configuration appears valid.'
        });
      } else {
        setTestResult({
          success: false,
          message: 'Connection test failed. Please verify your credentials and bucket configuration.'
        });
      }
    } catch (error) {
      console.error('Test connection error:', error);
      setTestResult({
        success: false,
        message: error?.message || 'Failed to test connection. Please check your configuration.'
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setSaveError('');
    
    try {
      const bucketData = {
        id: editBucket?.id,
        name: formData?.name,
        bucketName: formData?.bucketName,
        region: formData?.region,
        accessKey: formData?.accessKey,
        secretKey: formData?.secretKey,
        publicUrl: formData?.publicUrl,
        bucketType: editBucket?.bucketType || 'personal',
        isDefault: editBucket?.isDefault || false
      };

      await onSave(bucketData);
      handleClose();
    } catch (error) {
      console.error('Error saving bucket:', error);
      setSaveError(error?.message || 'Failed to save bucket configuration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      bucketName: '',
      region: 'us-east-1',
      accessKey: '',
      secretKey: '',
      publicUrl: ''
    });
    setErrors({});
    setSaveError('');
    setTestResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay animate-fade-in">
      <div className="modal-content max-w-2xl animate-scale-in" onClick={(e) => e?.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {editBucket ? 'Edit S3 Bucket Configuration' : 'Add New S3 Bucket'}
          </h2>
          <button onClick={handleClose} className="focus-ring rounded-md p-1 hover:bg-muted" disabled={isSaving}>
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="modal-body space-y-4">
          {saveError && (
            <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                <div className="text-sm">{saveError}</div>
              </div>
            </div>
          )}

          {testResult && (
            <div className={`rounded-lg p-4 ${
              testResult?.success 
                ? 'bg-success/10 border border-success text-success' :'bg-destructive/10 border border-destructive text-destructive'
            }`}>
              <div className="flex items-start gap-3">
                <Icon 
                  name={testResult?.success ? 'CheckCircle' : 'XCircle'} 
                  size={16} 
                  className="flex-shrink-0 mt-0.5" 
                />
                <div className="text-sm">{testResult?.message}</div>
              </div>
            </div>
          )}

          <Input
            label="Configuration Name"
            type="text"
            placeholder="e.g., Production Assets"
            description="A friendly name to identify this bucket"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
            disabled={isSaving}
          />

          <Input
            label="Bucket Name"
            type="text"
            placeholder="my-s3-bucket"
            description="The exact name of your S3 bucket"
            value={formData?.bucketName}
            onChange={(e) => handleChange('bucketName', e?.target?.value)}
            error={errors?.bucketName}
            required
            disabled={isSaving}
          />

          <Select
            label="AWS Region"
            description="Select the region where your bucket is located"
            options={regionOptions}
            value={formData?.region}
            onChange={(value) => handleChange('region', value)}
            searchable
            disabled={isSaving}
          />

          <Input
            label="Access Key ID"
            type="text"
            placeholder="AKIAIOSFODNN7EXAMPLE"
            description="Your AWS access key ID"
            value={formData?.accessKey}
            onChange={(e) => handleChange('accessKey', e?.target?.value)}
            error={errors?.accessKey}
            required
            disabled={isSaving}
          />

          <Input
            label="Secret Access Key"
            type="password"
            placeholder={editBucket ? "Leave blank to keep existing" : "Enter secret key"}
            description={editBucket ? "Only enter if you want to update the secret key" : "Your AWS secret access key"}
            value={formData?.secretKey}
            onChange={(e) => handleChange('secretKey', e?.target?.value)}
            error={errors?.secretKey}
            required={!editBucket}
            disabled={isSaving}
          />

          <Input
            label="Public URL"
            type="url"
            placeholder="https://my-bucket.s3.amazonaws.com"
            description="The public URL for accessing files in this bucket"
            value={formData?.publicUrl}
            onChange={(e) => handleChange('publicUrl', e?.target?.value)}
            error={errors?.publicUrl}
            required
            disabled={isSaving}
          />

          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p>• Ensure your AWS credentials have appropriate S3 permissions</p>
                <p>• The public URL should match your bucket's configured endpoint</p>
                <p>• Test the connection after saving to verify configuration</p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button 
            variant="outline" 
            onClick={handleClose} 
            disabled={isSaving || isTesting}
          >
            Cancel
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleTestConnection} 
            loading={isTesting}
            iconName="Zap"
            disabled={isSaving}
          >
            Test Connection
          </Button>
          <Button 
            onClick={handleSave} 
            loading={isSaving}
            disabled={isTesting}
          >
            {editBucket ? 'Update Configuration' : 'Add Bucket'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BucketConfigModal;